import React, { type ReactElement, useContext, useEffect } from "react";

import { LoadingBarContext } from "src/contexts";
import { type ServerError } from "src/graphql/fetcher";
import { getSpecificError } from "src/utils";

import { ErrorPage } from "../ErrorPage";
import { Loader } from "../Loader";

interface Props<T> {
  loading: boolean;
  data: T | null | undefined;
  error?: ServerError | null;
  refetching: boolean;
  refetch: () => void;
  children: ({ data }: { data: T }) => ReactElement | null;
}

export function Page<T>({ loading, data, error, refetching, refetch, children }: Props<T>) {
  const { continuousStart, complete } = useContext(LoadingBarContext);

  useEffect(() => {
    if (loading) {
      continuousStart();
    }

    if (!loading) {
      complete();
    }
  }, [loading]);

  if (loading && !data) {
    return <Loader />;
  }

  if (error || !data) {
    return (
      <ErrorPage
        title={
          error
            ? getSpecificError(error)
            : "Une erreur est survenue. Aucune donnée n'a pu être récupérée."
        }
        refetching={refetching}
        refetch={refetch}
      />
    );
  }

  return children({ data });
}
